import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Info, BadgeCheck, Plus, AlertTriangle } from "lucide-react";
import { InkAward, InkWallet, InkReceipt, InkSearch, InkIdCard, InkShield } from "./InkIcons";
import { useLang } from "../lib/lang";
import { DEB_URL, DEB_ID_URL, DISCLAIMER } from "../lib/copy";
import { brandify } from "./Brand";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";

export const Universities = () => {
  const { t } = useLang();
  const [list, setList] = useState([]);
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    axios.get(`${base}/api/institutes`).then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  }, []);
  return (
    <section id="universities" className="scroll-mt-20 py-24 lg:py-32" data-testid="universities-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow>{t.unis.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle>{t.unis.title}</SectionTitle></Reveal>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-night/80">{t.unis.note}</p>
        <a href={DEB_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex flex-wrap items-center gap-2 rounded-full bg-ink px-4 py-2 font-display text-sm font-bold text-white" data-testid="ugc-deb-badge">
          <BadgeCheck className="h-4 w-4 text-sun" aria-hidden="true" />{t.unis.badge}<span className="font-normal text-white/80">— {t.unis.badgeHint}</span><ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        {list.length === 0 ? (
          <p className="mt-10 rounded-2xl border border-ink/10 bg-white p-6 text-base text-night" data-testid="universities-empty">{t.unis.empty}</p>
        ) : (
          <ul className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((u) => (
              <li key={u.id} className="flex min-h-[84px] flex-col justify-center rounded-2xl border border-ink/10 bg-white px-4 py-4 transition-[border-color,box-shadow] duration-300 hover:border-ink/30 hover:shadow-[0_20px_40px_-28px_rgba(18,24,63,0.5)] sm:px-5" data-testid={`university-${u.id}`}>
                {u.official_url ? <a href={u.official_url} target="_blank" rel="noopener noreferrer" className="font-display text-sm font-bold text-ink underline underline-offset-4 sm:text-base">{u.name}</a> : <span className="font-display text-sm font-bold text-ink sm:text-base">{u.name}</span>}
                {u.last_verified_deb && <span className="mt-1 block text-xs text-night/80">{t.unis.verified} {u.last_verified_deb}</span>}
              </li>
            ))}
            <li className="flex min-h-[84px] items-center gap-2 rounded-2xl bg-sky px-4 py-4 font-display text-sm font-bold text-ink sm:px-5 sm:text-base" data-testid="university-more"><Plus className="h-4 w-4" aria-hidden="true" />{t.unis.more}</li>
          </ul>
        )}
        {list.length > 0 && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border-2 border-sun bg-sun/15 p-5" role="note" data-testid="universities-caution">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-ink" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-night sm:text-base"><strong className="font-display font-extrabold text-ink">{t.unis.cautionTitle}: </strong>{t.unis.caution}</p>
          </div>
        )}
        <a href={DEB_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold text-ink underline underline-offset-4" data-testid="universities-deb-link">{t.unis.debLink}<ExternalLink className="h-4 w-4" aria-hidden="true" /></a>
        <p className="mt-6 text-sm text-night" data-testid="universities-disclaimer">{brandify(DISCLAIMER)}</p>
      </div>
    </section>
  );
};

const FUND_ICONS = [InkAward, InkWallet, InkReceipt];

export const Funding = () => {
  const { t } = useLang();
  const f = t.funding;
  return (
    <section id="funding" className="scroll-mt-20 py-24 lg:py-32" data-testid="funding-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal className="relative">
          <div className="absolute -right-4 -top-6 h-32 w-32 rounded-full bg-sun sm:h-40 sm:w-40" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[2rem] rounded-tr-[6rem]">
            <img src="/img/graduation.webp" alt={f.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </div>
          <p className="relative mt-2 text-xs italic text-night/70" data-testid="funding-img-note">{t.imgNote}</p>
        </Reveal>
        <div>
          <Reveal><Eyebrow>{f.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionTitle>{f.title}</SectionTitle></Reveal>
          <ul className="mt-10 space-y-4">
            {f.items.map((it, i) => {
              const Icon = FUND_ICONS[i];
              return (
                <li key={it.title} className="flex gap-4 rounded-2xl border border-ink/10 bg-white p-5" data-testid={`funding-item-${i}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sun text-night"><Icon className="h-7 w-7" accent="#FFFFFF" /></span>
                  <span><span className="block font-display text-lg font-extrabold text-ink">{it.title}</span><span className="mt-1 block text-base text-night">{it.text}</span></span>
                </li>
              );
            })}
          </ul>
          <p className="mt-5 text-sm text-night">{f.note}</p>
        </div>
      </div>
    </section>
  );
};

const ICONS = [InkSearch, InkIdCard, InkShield];

export const Verify = () => {
  const { t } = useLang();
  const v = t.verify;
  return (
    <section id="verify" className="scroll-mt-20 bg-white py-24 lg:py-32" data-testid="verify-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow>{v.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.05}><SectionTitle>{v.title}</SectionTitle></Reveal>
        <ol className="mt-12 grid gap-5 md:grid-cols-2">
          {v.items.map((it, i) => {
            const Icon = ICONS[i];
            return (
              <li key={it} className="flex gap-5 rounded-[1.75rem] bg-sky/70 p-6 sm:p-7" data-testid={`verify-item-${i}`}>
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ink text-white"><Icon className="h-8 w-8" /></span>
                <p className="text-base leading-relaxed text-night"><span className="mr-2 font-display font-extrabold text-ink">{i + 1}.</span>{it}</p>
              </li>
            );
          })}
        </ol>
        <p className="mt-8 rounded-2xl bg-ink px-6 py-5 font-display text-lg font-extrabold text-white sm:text-xl" data-testid="never-pay-notice">{v.never}</p>
        <div className="mt-6 flex flex-wrap gap-4">
          <a href={DEB_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-3 font-display text-sm font-bold text-ink hover:bg-sky" data-testid="verify-deb-link">{v.deb}<ExternalLink className="h-4 w-4" aria-hidden="true" /></a>
          <a href={DEB_ID_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-3 font-display text-sm font-bold text-ink hover:bg-sky" data-testid="verify-debid-link">{v.debId}<ExternalLink className="h-4 w-4" aria-hidden="true" /></a>
        </div>
      </div>
    </section>
  );
};

export const HomeDisclaimer = () => (
  <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
    <p className="flex items-start gap-3 rounded-2xl border border-ink/15 bg-white p-5 text-sm leading-relaxed text-night" data-testid="home-disclaimer">
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-ink" aria-hidden="true" />{brandify(DISCLAIMER)}
    </p>
  </div>
);
